import { BindingEngine, autoinject, transient } from "aurelia-framework";
import { RightShipInspectionClientModel } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { BaseOfficeTimeBaseEntity } from "../../../common-steps/base-office-time";
import { StepModel } from "../../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../../../models/validations/validations.models";
import { UnitOfWorkItem } from "../../../../../../utilities/unit-of-work-observer";
import { ValidationController, ValidationControllerFactory, ValidationRules, validateTrigger } from "aurelia-validation";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Router } from "aurelia-router";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { I18N } from "aurelia-i18n";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { EmailValidator } from "../../../../../../utilities/email-validator";

@autoinject
@transient()
export class ResendRightShipInspectionRequiredEmailDialog extends BaseOfficeTimeBaseEntity<RightShipInspectionClientModel> {
    public vesselName: string;
    public imo: string;
    public vesselId: number;
    public reasonForInspectionId: number;
    public comments: string = "";
    public technicalManagerEmail: string;
    public correspondenceEmail: string;
    public correspondenceMessage: string = "";
    public emails: { email: string }[] = [];
    public validator: ValidationController;

    constructor(private rightShipInspectionService: RightShipInspectionsService, private dialogClientService: DialogClientService, router: Router,
        bindingEngine: BindingEngine, i18N: I18N, notificationService: NotificationClientService, userClientService: UserClientService,
        eventAggregator: EventAggregator, validationsClientService: ValidationsClientService, validationControllerFactory: ValidationControllerFactory) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());
        this.validator.changeTrigger(validateTrigger.manual);
    }

    public activate(model: any, navigationInstruction: any): Promise<void> {
        this.item = model.item;
        this.vesselId = this.item.vesselId;
        this.vesselName = this.item.vesselName;
        this.imo = this.item.vesselImo;
        this.reasonForInspectionId = this.item.reasonForInspectionId;
        this.technicalManagerEmail = this.item.technicalManagerEmail;
        this.correspondenceEmail = this.item.correspondenceEmail;
        if (this.item.additionalEmailsToNotify && this.item.additionalEmailsToNotify != "") {
            const additionalEmails = this.item.additionalEmailsToNotify.split(";");
            additionalEmails.forEach(email => {
                this.emails.push({ email: email });
            });
        }
        this.correspondenceMessage = `This message is sent as subject vessel has been marked for a RightShip inspection. An inspection request is entirely a matter for Owners where the following should be considered:
            <br>- Under current RightShip rules vessels over 8,000 DWT and 14 years of age, and all vessels over 25 years of age, must have a valid RightShip inspection for the vessel to be recommended to RightShip customers.
            <br>- Some RightShip customers have inspection requirements that exceed the standard RightShip rules (e.g irrespective of vessel’s age).
            <br>- Having a valid RightShip inspection badge, whether required by the above or not, may boost confidence in a vessel’s performance and upholding the industry safety standards. This will be lost at the time of inspection expiry.
            <br>An Inspection application can be submitted from the RightShip website by clicking on the button below.
            <br>`;

        model.isFromDialog = true;    

        return super.activate(model, navigationInstruction);
    }

    protected getItem(id: number): Promise<RightShipInspectionClientModel> {
        return new Promise((resolve, reject) => {
            this.rightShipInspectionService.getRightShipInspectionClientModel(id).then(resolve).catch(reject);
        })
    }

    protected saveItem(id: number, changes: { [index: string]: UnitOfWorkItem; }): Promise<BaseValidationResponse> {
        return this.rightShipInspectionService.resendInspectionRequiredEmail(
            id,            
            this.correspondenceMessage,
            changes
        );
    }

    public async beforeSave(skipValidation: boolean, resolve: (isValid: boolean) => void) {
        this.validator.validate().then(r => {
            if (r.valid) {
                if (this.emails && this.emails.filter(x => x.email != null && x.email.trim() != '').length > 0) {
                    //this.item.correspondenceEmail = this.emails[0].email;
                    const validEmails = this.emails.filter(x => x.email != null && x.email.trim() != '')
                        //.slice(1) //filter out first email which would be set in the correspondence email
                        .map(x => x.email);
                    if (validEmails.length > 0) {
                        this.item.additionalEmailsToNotify = validEmails.join(';');
                    } else {
                        this.item.additionalEmailsToNotify = "";
                    }
                }
                if(this.technicalManagerEmail && this.technicalManagerEmail != this.item.technicalManagerEmail){
                    this.item.technicalManagerEmail = this.technicalManagerEmail;
                }
                if(this.correspondenceEmail && this.correspondenceEmail != this.item.correspondenceEmail){
                    this.item.correspondenceEmail = this.correspondenceEmail;
                }
                resolve(true);
            } else resolve(false);
        });
    }

    protected getUpdateRoute(): string {
        return null;
    }
    protected getEntityType(): EntityTypes {
        return EntityTypes.RightShipInspection;
    }
    protected handleUpdateCompleted(response: any, isNew: boolean) {
        if (this._params.reload)
            this._params.reload();
        super.handleUpdateCompleted(response, isNew);
    }
    protected initializeItemForCreate(): RightShipInspectionClientModel | Promise<RightShipInspectionClientModel> {
        return this.item;
    }

    protected close() {
        this.dialogClientService.hide();
    }

    protected getListRoute(): string {
        return null;
    }

    protected getSteps(): StepModel[] {
        return [];
    }

    protected getItemId(): number {
        return this.item.rightShipInspectionId;
    }

    removeEmail(index) {
        const deleted = this.emails.splice(index, 1);
        this.validator.removeObject(deleted[0]);
    }

    getEmailRules(email: { email: string }) {
        const rules = ValidationRules.ensure((x: { email: string }) => x.email)
            .satisfies(x => {
                return !(x == null || x.trim() == '');
            })
            .withMessage("Email Required")
            .satisfies(x => EmailValidator.isValidEmail(x))
            .on(email).rules;

        return rules;
    }

    addEmail() {
        const email = {
            email: ''
        };
        this.emails.push(email);

        const rules = this.getEmailRules(email);
        this.validator.addObject(email, rules);
    }
}